// src/firebase.ts
import { initializeApp, FirebaseApp } from "firebase/app";
import { getAuth, Auth } from "firebase/auth";

let firebaseApp: FirebaseApp | null = null;
let auth: Auth | null = null;
let stripePublishableKey: string | null = null; // Renamed for clarity

interface AppConfig {
  firebaseApp: FirebaseApp;
  auth: Auth;
  stripeKey: string | null;
}

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
};

const fetchAppConfig = async (): Promise<AppConfig> => {
  if (firebaseApp && auth && stripePublishableKey) {
    return { firebaseApp, auth, stripeKey: stripePublishableKey };
  }

  // Initialize Firebase only if not already done
  if (!firebaseApp) {
    firebaseApp = initializeApp(firebaseConfig);
    auth = getAuth(firebaseApp);
  }

  // Get Stripe key from env
  stripePublishableKey = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || null;

  return { firebaseApp: firebaseApp!, auth: auth!, stripeKey: stripePublishableKey };
};

/**
 * Primarily for getting the initialized Firebase `auth` instance.
 * Keeps original signature for compatibility.
 */
export const getAuthInstance = async (): Promise<Auth> => {
  const { auth: fetchedAuth } = await fetchAppConfig();
  return fetchedAuth;
};

/**
 * Exported for potential use if direct app access is needed,
 * though getAuthInstance and getStripePublishableKey are preferred.
 * Returns the core Firebase app and auth for broader use if necessary.
 */
export const getFirebase = async (): Promise<{ firebaseApp: FirebaseApp, auth: Auth }> => {
    const { firebaseApp: app, auth: anAuth } = await fetchAppConfig();
    return { firebaseApp: app, auth: anAuth };
};

/**
 * Use this to get the initialized Stripe Publishable Key.
 */
export const getStripeKey = async (): Promise<string | null> => {
  const { stripeKey: key } = await fetchAppConfig();
  return key;
};